import type { JsaPhoto } from 'src/app/state-man/models/jsa-photo.model'
import { IsoDate, mdy } from './dates'
import { FORM_CATALOG, FormCode } from './form-catalog'
import { JsaDay, JsaItem, PAPER_KINDS, PaperItem, PaperKind } from './paper'
import { SCREEN_ROUTE, ScreenKey } from './screens'
import type { PaperStatus } from './status'
import type { ToolsTab } from './tools'

/** Built only by `links.*`, so every navigation goes through a builder and ShellService.go(). */
declare const navBrand: unique symbol
export interface NavTarget {
  readonly [navBrand]: true
  readonly url: string                                   // '/tabs/…', segments already encoded
  readonly query?: Readonly<Record<string, string>>
  /** forward: push (Back returns here) · root: reset the stack (menu, Callouts, How To) · replace: swap the top (Form Picker → editor) */
  readonly mode: 'forward' | 'root' | 'replace'
}

/** Route patterns under /tabs, declared once for shell.routes.ts (path:) and the builders below (fill). */
export const ROUTE = {
  schedule: 'schedule',
  scope: 'scope-of-work/:work_order_id',
  paperDsr: 'paperwork/dsr',
  paperPrf: 'paperwork/prf',
  paperForms: 'forms',
  paperJob: 'paperwork/:kind/job/:woNumber',             // ?date=yyyy-MM-dd, the start date for "+ New"
  formGroup: 'forms/group/:group_uuid',                  // one Add Another / Duplicate chain (copies are saved on the list: domain/form-copy.ts)
  formPicker: 'form-picker/:woNumber/:date',
  formNew: 'forms/:slug-form/:work_order_id/:selected_date',
  formEdit: 'forms/:slug-form/:uuid/:work_order_id/:selected_date/:group_uuid/:group_number',
  dsrNew: 'dsr/add-:type-dsr/:work_order_id/:selected_date',            // type: bt | nbt
  dsrEdit: 'dsr/view-edit-:type-dsr/:uuid/:work_order_id/:selected_date',
  prfNew: 'prf/add-prf/:work_order_id',                                  // ?date=yyyy-MM-dd
  prfEdit: 'prf/view-edit-prf/:uuid',
  jsaNew: 'jsa/:jsa_param_id/:add',                                      // add = 'add-form'
  jsaEdit: 'jsa/:jsa_param_id/:jsa_id/:uuid',
  jsaPhoto: 'jsa/view/:jsa_param_id/:jsa_id/:photo_id',
  worList: 'wor', worNew: 'wor/add-wor', worDuplicate: 'wor/add-wor/:duplicate_uuid', worEdit: 'wor/view-edit-wor/:uuid',
  timesheet: 'time-sheet',
  expenses: 'expenses', expenseReport: 'expenses/:expense_report_uuid',
  expenseNewReport: 'expenses/add-expense-report/:new_expense_report',
  expenseNewLine: 'expenses/add-expense-line/:expense_report_uuid',
  expenseEditLine: 'expenses/view-edit-expense-line/:expense_report_uuid/:expense_line_uuid',
  pto: 'pto-page', howto: 'howto', support: 'support', supportNew: 'support/add-ticket', about: 'about',
  tools: 'tools', toolsTab: 'tools/:tab', toolCount: 'tools/count/:id',
} as const

/** Expand a pattern, ':slug-form' style partial tokens included. Encodes each value; a missing value throws. */
export function fill(pattern: string, params: Readonly<Record<string, string | number>>): string {
  return pattern.replace(/:([A-Za-z_]+)/g, (_, key: string) => {
    const value = params[key]
    if (value === undefined || value === null || value === '') throw new Error(`fill: no value for :${key} in ${pattern}`)
    return encodeURIComponent(String(value))
  })
}

/** '/tabs/…' path of a menu screen. */
export function screenPath(k: ScreenKey): string {
  return `/tabs/${ROUTE[SCREEN_ROUTE[k]]}`
}

/** A scheduled job as the links need it. */
export interface JobRef {
  readonly woId: number
  readonly woNumber: string
  /** 'Roto-versal Indirect Time' → Indirect Time DSR (nbt routes). */
  readonly indirect: boolean
}

/** Indirect time is booked to work order 1 (HOU-55555) and to other work orders under this customer. */
export const isIndirectJob = (woId: number | null | undefined, customer: string | null | undefined): boolean =>
  woId === 1 || customer === 'Roto-versal Indirect Time'

const to = (path: string, mode: NavTarget['mode'], query?: Readonly<Record<string, string>>): NavTarget =>
  ({ url: `/tabs/${path}`, mode, ...(query ? { query } : {}) }) as NavTarget

const formSlug = (code: FormCode | null, rawName: string) => (code ? FORM_CATALOG[code].slug : rawName.toLowerCase())

// The JSA page reads the first id of `j{jsaId}_o{otherPhotosId}` as the row it works on.
const jsaParam = (item: JsaItem) =>
  item.ref.as === 'photos' && item.ref.type === 'Other Photos' ? `o${item.ref.jsa.id}_j0` : `j${item.ref.jsa.id}_o0`

const jsaPhotoTarget = (item: JsaItem, photoId: number) =>
  to(fill(ROUTE.jsaPhoto, { jsa_param_id: jsaParam(item), jsa_id: item.ref.jsa.id, photo_id: photoId }), 'forward')

// Form and DSR editors parse :selected_date as M/d/yyyy.
const screen = (k: ScreenKey, query?: Record<string, string>): NavTarget => to(ROUTE[SCREEN_ROUTE[k]], 'root', query)

export const links = {
  screen,
  paperList: (kind: 'dsr' | 'prf' | 'form', status?: PaperStatus): NavTarget =>
    screen(kind === 'dsr' ? 'paper-dsr' : kind === 'prf' ? 'paper-prf' : 'paper-forms', status ? { status } : undefined),
  jobPaper: (kind: PaperKind, woNumber: string, date: IsoDate | null): NavTarget =>
    to(fill(ROUTE.paperJob, { kind: PAPER_KINDS[kind].slug, woNumber }), 'forward', date ? { date } : undefined),
  /** `code` names the group in the Back label of the editors opened from it. */
  formGroup: (groupUuid: string, code: string): NavTarget => to(fill(ROUTE.formGroup, { group_uuid: groupUuid }), 'forward', { code }),
  formPicker: (job: JobRef, date: IsoDate): NavTarget => to(fill(ROUTE.formPicker, { woNumber: job.woNumber, date }), 'forward'),
  /** Edit or View: same route; the editor reads the status and locks itself. */
  openPaper: (item: PaperItem): NavTarget => {
    switch (item.kind) {
      case 'dsr':
        return to(fill(ROUTE.dsrEdit, { type: item.indirect ? 'nbt' : 'bt', uuid: item.uuid, work_order_id: item.ref.work_order_id, selected_date: mdy(item.worked) }), 'forward')
      case 'form':
        return to(fill(ROUTE.formEdit, {
          slug: formSlug(item.code, item.ref.form_name), uuid: item.uuid, work_order_id: item.ref.work_order_id,
          selected_date: mdy(item.worked), group_uuid: item.groupUuid, group_number: item.segment,
        }), 'forward')
      case 'prf':
        return to(fill(ROUTE.prfEdit, { uuid: item.uuid }), 'forward')
      case 'jsa': {
        const ref = item.ref
        if (ref.as === 'form') return to(fill(ROUTE.jsaEdit, { jsa_param_id: jsaParam(item), jsa_id: ref.jsa.id, uuid: ref.form.uuid }), 'forward')
        return jsaPhotoTarget(item, ref.photos[0].id)
      }
    }
  },
  /** "+ New …" and job-card Add: forms → picker, jsa → digital JSA, dsr → bt | nbt by job.indirect. null when it can't be created (no JSA row that day). */
  newPaper: (kind: PaperKind, job: JobRef, date: IsoDate, jsaDay?: JsaDay): NavTarget | null => {
    switch (kind) {
      case 'dsr': return to(fill(ROUTE.dsrNew, { type: job.indirect ? 'nbt' : 'bt', work_order_id: job.woId, selected_date: mdy(date) }), 'forward')
      case 'prf': return to(fill(ROUTE.prfNew, { work_order_id: job.woId }), 'forward', { date })
      case 'form': return links.formPicker(job, date)
      case 'jsa': return jsaDay?.jsa
        ? to(fill(ROUTE.jsaNew, { jsa_param_id: `j${jsaDay.jsa.id}_o${jsaDay.other?.id ?? 0}`, add: 'add-form' }), 'forward')
        : null
    }
  },
  /** Replaces the Form Picker, so Back from the editor skips it. */
  newForm: (code: FormCode, job: JobRef, date: IsoDate): NavTarget =>
    to(fill(ROUTE.formNew, { slug: FORM_CATALOG[code].slug, work_order_id: job.woId, selected_date: mdy(date) }), 'replace'),
  jsaPhoto: (item: JsaItem, photo: JsaPhoto): NavTarget => jsaPhotoTarget(item, photo.id),
  scope: (woId: number): NavTarget => to(fill(ROUTE.scope, { work_order_id: woId }), 'forward'),
  newWor: (): NavTarget => to(ROUTE.worNew, 'forward'),
  wor: (uuid: string): NavTarget => to(fill(ROUTE.worEdit, { uuid }), 'forward'),
  duplicateWor: (uuid: string): NavTarget => to(fill(ROUTE.worDuplicate, { duplicate_uuid: uuid }), 'forward'),
  // Expense keys are a report's or line's uuid, or its server id when the API row carries no uuid.
  expenseReport: (reportKey: string): NavTarget => to(fill(ROUTE.expenseReport, { expense_report_uuid: reportKey }), 'forward'),
  newExpenseReport: (): NavTarget => to(fill(ROUTE.expenseNewReport, { new_expense_report: 'true' }), 'forward'),
  newExpenseLine: (reportKey: string): NavTarget => to(fill(ROUTE.expenseNewLine, { expense_report_uuid: reportKey }), 'forward'),
  expenseLine: (reportKey: string, lineKey: string): NavTarget =>
    to(fill(ROUTE.expenseEditLine, { expense_report_uuid: reportKey, expense_line_uuid: lineKey }), 'forward'),
  newTicket: (): NavTarget => to(ROUTE.supportNew, 'forward'),
  /** Tool Inventory on a tab. `status` pre-filters Tool Counts; `sign` opens the sign dialog on that handoff. */
  tools: (tab: ToolsTab = 'mine', query?: Record<string, string>): NavTarget =>
    to(tab === 'mine' ? ROUTE.tools : fill(ROUTE.toolsTab, { tab }), 'root', query),
  toolCount: (id: number): NavTarget => to(fill(ROUTE.toolCount, { id }), 'forward'),
} as const
